import { RightChevronIcon } from 'src/icons';
import { PopoverOrigin } from '@mui/material';
import { createTheme } from 'src/lib/style/theme';
import palette, { Color } from './constants/palette';

export const baseTheme = createTheme({
    palette: {
        primary: {
            main: palette.primary,
            contrastText: palette.textLight,
        },
        secondary: {
            main: palette.primaryBlue,
            contrastText: palette.textLight,
        },
        // custom colors provided via resqPalette
        success: {
            main: Color.Green600,
            contrastText: Color.White,
        },
        warning: {
            main: Color.Yellow600,
            contrastText: Color.White,
        },
        info: {
            main: Color.Blue500,
            contrastText: Color.White,
        },
        // neutral removed; use resqPalette instead
        error: {
            main: Color.Red500,
            contrastText: Color.White,
        },
        // payment removed from MUI palette; use resqPalette if needed
        text: {
            primary: palette.textPrimary,
            secondary: palette.textSecondary,
        },
        divider: Color.Grey200,
        background: {
            default: palette.background,
        },
    },
    typography: {
        fontFamily: ['Inter', 'Helvetica', 'Arial', 'sans-serif'].join(','),
        h1: {
            fontSize: '24px',
            fontWeight: 600,
            color: palette.textPrimary,
        },
        h2: {
            fontSize: '20px',
            fontWeight: 600,
            color: palette.textPrimary,
        },
        h3: {
            fontSize: '18px',
            fontWeight: 600,
            color: palette.textPrimary,
            lineHeight: '1.5',
        },
        h4: {
            fontSize: '16px',
            fontWeight: 600,
            color: palette.textPrimary,
            lineHeight: '1.5',
        },
        h5: {
            fontSize: '14px',
            fontWeight: 500,
            color: palette.textPrimary,
            lineHeight: '1.5',
        },
        subtitle1: {
            fontSize: '16px',
            fontWeight: 400,
            color: palette.textSecondary,
            lineHeight: '1.5',
        },
        subtitle2: {
            fontSize: '14px',
            fontWeight: 400,
            color: palette.textSecondary,
            lineHeight: '1.5',
        },
        caption: {
            fontSize: '13px',
            fontWeight: 400,
            color: palette.textSecondary,
            lineHeight: '1.5',
        },
        body1: {
            fontSize: '16px',
            fontWeight: 400,
            color: palette.textPrimary,
            lineHeight: '1.5',
        },
        body2: {
            fontSize: '14px',
            fontWeight: 400,
            color: palette.textPrimary,
            lineHeight: '1.5',
        },
        body3: {
            display: 'block',
            fontSize: '13px',
            fontWeight: 400,
            color: palette.textSecondary,
            lineHeight: '1.3',
        },
    },
});

baseTheme.components = {
    MuiDialog: {
        defaultProps: {
            fullWidth: true,
        },
    },
    MuiCollapse: {
        defaultProps: {
            timeout: 375,
        },
    },
    MuiAppBar: {
        styleOverrides: {
            root: {
                colorPrimary: {
                    color: baseTheme.resqPalette.text.default,
                },
            },
        },
    },
    MuiBreadcrumbs: {
        defaultProps: {
            separator: <RightChevronIcon height="24px" />,
        },
        styleOverrides: {
            li: {
                '&:last-child': {
                    color: baseTheme.resqPalette.text.default,
                },
                '& p': {
                    fontWeight: 600,
                },
                '& svg': {
                    marginRight: baseTheme.spacing(1.5),
                },
            },
            root: {
                marginBottom: baseTheme.spacing(4),
                fontWeight: 600,
                '& a': {
                    color: palette.link,
                    '&:hover': {
                        textDecoration: 'underline',
                    },
                },
            },
            separator: {
                marginRight: baseTheme.spacing(0.5),
                marginLeft: baseTheme.spacing(0.5),
            },
        },
    },
    MuiButton: {
        defaultProps: {
            color: 'neutral',
        },
        styleOverrides: {
            contained: {
                boxShadow: 'none',
            },
            root: {
                textTransform: 'unset',
                transitionProperty: 'background-color, box-shadow, border, color',
                whiteSpace: 'nowrap',
                '&.MuiButton-textNeutral': {
                    color: baseTheme.resqPalette.text.default,
                },
                '&.MuiButton-outlinedNeutral': {
                    color: baseTheme.resqPalette.text.default,
                },
            },
        },
    },
    MuiAlert: {
        styleOverrides: {
            standardInfo: {
                backgroundColor: baseTheme.resqPalette.surface.highlightSubdued,
                border: `1px solid ${baseTheme.resqPalette.border.highlight}`,
                color: baseTheme.resqPalette.text.default,
                '.MuiAlert-icon': {
                    color: baseTheme.resqPalette.icon.highlight,
                },
            },
            standardError: {
                backgroundColor: baseTheme.resqPalette.surface.criticalSubdued,
                border: `1px solid ${baseTheme.resqPalette.border.critical}`,
                '.MuiAlert-icon': {
                    color: baseTheme.resqPalette.icon.critical,
                },
            },
            standardWarning: {
                backgroundColor: baseTheme.resqPalette.surface.warningSubdued,
                border: `1px solid ${baseTheme.resqPalette.border.warning}`,
                '.MuiAlert-icon': {
                    color: baseTheme.resqPalette.icon.warning,
                },
            },
            root: {
                paddingTop: 0,
                paddingBottom: 0,
                '.MuiButton-textNeutral': {
                    marginTop: baseTheme.spacing(1),
                    color: `${palette.warningHiglightBrown} !important`,
                    borderColor: palette.warningHiglightBrown,
                    boxShadow: baseTheme.spacing(10),
                    '&:hover': {
                        borderColor: palette.warningHiglightBrown,
                    },
                },
                '.MuiButton-outlinedNeutral': {
                    marginTop: baseTheme.spacing(1),
                    color: `${palette.warningHiglightBrown} !important`,
                    borderColor: palette.warningHiglightBrown,
                    boxShadow: baseTheme.spacing(10),
                    '&:hover': {
                        borderColor: palette.warningHiglightBrown,
                    },
                },
            },
        },
    },
    MuiButtonGroup: {
        styleOverrides: {
            contained: {
                boxShadow: 'none',
            },
        },
    },

    MuiFormLabel: {
        styleOverrides: {
            root: {
                color: baseTheme.resqPalette.text.default,
            },
        },
    },
    MuiInput: {
        styleOverrides: {
            underline: {
                '&.Mui-disabled::before': {
                    borderBottomColor: 'transparent',
                    borderBottomStyle: 'solid',
                },
            },
        },
    },
    MuiInputAdornment: {
        styleOverrides: {
            positionStart: {
                marginRight: 0,
            },
        },
    },
    MuiInputBase: {
        styleOverrides: {
            input: {
                '&::placeholder': {
                    color: baseTheme.resqPalette.text.placeHolder,
                    WebkitTextFillColor: baseTheme.resqPalette.text.placeHolder,
                    opacity: 1,
                },
                '&::-moz-placeholder': {
                    color: baseTheme.resqPalette.text.placeHolder,
                    opacity: 1,
                },
                '&:-ms-input-placeholder': {
                    color: baseTheme.resqPalette.text.placeHolder,
                    opacity: 1,
                },
                '&::-webkit-input-placeholder': {
                    color: baseTheme.resqPalette.text.placeHolder,
                    WebkitTextFillColor: baseTheme.resqPalette.text.placeHolder,
                    opacity: 1,
                },
            },
            root: {
                // keep default disabled color; adornments hidden by opacity
                '&.Mui-disabled .MuiSelect-icon, &.Mui-disabled .MuiAutocomplete-endAdornment': {
                    opacity: 0,
                },
            },
        },
    },
    MuiFormControl: {
        defaultProps: {
            variant: 'standard',
        },
    },
    MuiTextField: {
        defaultProps: {
            variant: 'standard',
            fullWidth: true,
        },
    },
    MuiOutlinedInput: {
        defaultProps: {
            notched: false,
            label: null,
        },
        styleOverrides: {
            root: {
                '[disabled]': {
                    backgroundColor: Color.Grey50,
                    color: Color.Grey400,
                },
            },
        },
    },
    MuiInputLabel: {
        defaultProps: {
            shrink: true,
        },
        styleOverrides: {
            root: {
                color: baseTheme.palette.text.primary,
            },
        },
    },
    MuiPaper: {
        styleOverrides: {
            rounded: {
                borderRadius: '8px',
                boxShadow: `0 4px 8px ${palette.shadow} !important`,
                overflow: 'hidden',
                [baseTheme.breakpoints.down('lg')]: {
                    borderRadius: '0',
                    borderColor: 'transparent',
                    boxShadow: 'none !important',
                },
            },
        },
    },
    MuiSelect: {
        defaultProps: {
            variant: 'standard',
            MenuProps: {
                anchorOrigin: {
                    vertical: 'bottom' as PopoverOrigin['vertical'],
                    horizontal: 'center' as PopoverOrigin['horizontal'],
                },
                transformOrigin: {
                    vertical: 'top',
                    horizontal: 'center',
                },
            },
        },
        styleOverrides: {
            icon: {
                width: '0.85em',
                height: '0.85em',
                top: 'calc(50% - 10px)',
            },
            select: {
                '&.Mui-disabled': {
                    userSelect: 'auto',
                    backgroundColor: Color.Grey50,
                    color: Color.Grey400,
                },
            },
        },
    },
    MuiSvgIcon: {
        styleOverrides: {
            fontSizeLarge: {
                fontSize: 28,
            },
        },
    },
    MuiListItemText: {
        styleOverrides: {
            root: {
                marginTop: baseTheme.spacing(1),
                marginBottom: baseTheme.spacing(1),
            },
        },
    },
    MuiTab: {
        styleOverrides: {
            root: {
                textTransform: 'unset',
                fontSize: '16px',
                fontWeight: 600,
                opacity: 1,
                color: baseTheme.resqPalette.text.subdued,
                minWidth: 'unset !important',
                maxWidth: 'unset !important',
                minHeight: 'unset !important',
                lineHeight: 'unset !important',
                padding: baseTheme.spacing(0, 0, 1),
                marginRight: baseTheme.spacing(4),
                '&.Mui-focused': {
                    color: baseTheme.resqPalette.text.default,
                },
                '&.MuiTab-textColorSecondary.Mui-selected': {
                    color: baseTheme.resqPalette.text.default,
                },
            },
        },
    },
    MuiTabs: {
        defaultProps: {
            indicatorColor: 'secondary',
            textColor: 'secondary',
        },

        styleOverrides: {
            indicator: {
                height: '3px',
            },
            root: {
                minHeight: 'unset',
                [baseTheme.breakpoints.down('lg')]: {
                    paddingLeft: baseTheme.spacing(4),
                    paddingRight: baseTheme.spacing(4),
                },
            },
        },
    },
    MuiTable: {
        styleOverrides: {
            root: {
                borderCollapse: 'separate',
            },
        },
    },
    MuiTableCell: {
        styleOverrides: {
            head: {
                backgroundColor: Color.Grey50,
                color: baseTheme.resqPalette.text.default,
                fontSize: '14px',
                fontWeight: 500,
                verticalAlign: 'bottom',
                lineHeight: 1.3,
            },
            body: {
                borderTop: `1px solid ${baseTheme.resqPalette.divider.default}`,
            },
            footer: {
                borderTop: `1px solid ${baseTheme.resqPalette.divider.default}`,
            },
            root: {
                borderBottom: 'none',
                '& a': {
                    textDecoration: 'underline',
                },
                '&:first-of-type': {
                    paddingLeft: baseTheme.spacing(4),
                },
                '&:last-child': {
                    paddingRight: baseTheme.spacing(4),
                },
            },
        },
    },
    MuiTablePagination: {
        styleOverrides: {
            toolbar: {
                padding: baseTheme.spacing(0, 4),
                justifyContent: 'flex-end',
                flexWrap: 'wrap',
            },
            spacer: {
                display: 'none',
            },
        },
    },
    MuiToolbar: {
        styleOverrides: {
            regular: {
                [baseTheme.breakpoints.up('sm')]: {
                    minHeight: '48px !important',
                },
            },
        },
    },
    MuiTooltip: {
        styleOverrides: {
            tooltip: {
                fontSize: '12px',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
            },
        },
    },
    MuiTypography: {
        styleOverrides: {
            body1: {
                '& a': {
                    textDecoration: 'underline',
                },
            },
        },
    },
    MuiDialogTitle: {
        styleOverrides: {
            root: {
                padding: baseTheme.spacing(3, 4),
                fontWeight: 600,
                fontSize: '1.375rem',
                [baseTheme.breakpoints.down('md')]: {
                    padding: baseTheme.spacing(4, 3, 3),
                },
            },
        },
    },
    MuiDialogContent: {
        styleOverrides: {
            root: {
                padding: baseTheme.spacing(4),
                [baseTheme.breakpoints.down('md')]: {
                    padding: baseTheme.spacing(4, 3),
                },
            },
        },
    },
    MuiDialogActions: {
        styleOverrides: {
            root: {
                padding: baseTheme.spacing(0, 4, 3),
                justifyContent: 'space-between',
                [baseTheme.breakpoints.down('md')]: {
                    padding: baseTheme.spacing(2, 3),
                    backgroundColor: baseTheme.resqPalette.background.default,
                },
            },
        },
    },
};
